import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { NotificationStreamItem } from "@situate/contracts";
import type { NotificationsListResponse } from "@situate/contracts/runtime";
import type { AlertStatus } from "@situate/contracts/firestore";
import { notificationsListResponseSchema } from "@situate/contracts/runtime-zod";
import { useNotificationsStream } from "@/features/deployment-dashboard/notification-center/hooks/use-notifications-stream";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import type { NotificationPopupQueueItem } from "@/features/deployment-dashboard/notification-center/notification-popup-stack";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { canViewNotifications } from "@/features/deployment-selection/model/permissions";

export interface NotificationCenterContextValue {
  /** Whether the layout-level notification rail is currently expanded. */
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;

  /** Streamed notifications from the runtime notification SSE subscription,
   * newest `created_at` first. This powers the bell and rail only; the Notifications
   * page uses the filtered REST history feed. */
  liveNotifications: readonly NotificationStreamItem[];
  /** Number of active alert notifications. */
  unresolvedAlertCount: number;
  /** Alert lifecycle status keyed by notification ID, shared by the bell cards and popups. */
  alertStatuses: ReadonlyMap<string, AlertStatus | null>;
  /** The notification ID a popup click (or a rail row click) has requested be
   * highlighted/scrolled into view in the bell panel. */
  selectedLiveNotificationId: string | null;
  /** Sets the highlighted live notification without otherwise changing panel/unseen state. */
  selectLiveNotification: (notificationId: string | null) => void;
  /** Popups awaiting or currently occupying a visible slot, oldest first. */
  popupQueue: readonly NotificationPopupQueueItem[];
  /** A popup's visible timer elapsed; removes it without marking anything seen. */
  onExpireNotificationPopup: (popupId: string) => void;
  /** A popup was clicked: dismisses it, navigates to the notifications page, and
   * expands alert details when the popup represents an alert. */
  onSelectNotificationPopup: (popupId: string) => void;
}

const NotificationCenterContext = createContext<NotificationCenterContextValue | null>(null);

interface NotificationCenterProviderProps {
  children: ReactNode;
}

/**
 * Owns the deployment-wide notification center state shared by the top bar bell and the
 * layout-level right rail: the SSE-backed live notification store (streamed list,
 * frontend-only unseen IDs, and popup queue), plus the rail's collapsed/expanded state
 * (collapsed by default) and the currently highlighted notification. Its subscription
 * lifecycle is independent of whether the bell/rail is currently visible.
 */
export function NotificationCenterProvider({ children }: NotificationCenterProviderProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const navigate = useNavigate();

  const { deploymentId, selectedDeployment } = useSelectedDeployment();
  const queryClient = useQueryClient();
  const runtime = useRuntimeHttpClient();
  const canViewNotificationData = canViewNotifications(selectedDeployment);
  const stream = useNotificationsStream(deploymentId, canViewNotificationData);
  const alertFeedQuery = useQuery({
    queryKey: ["notification-alert-count", deploymentId],
    queryFn: async (): Promise<NotificationsListResponse> => {
      if (runtime.client === null) throw new Error("Runtime client is unavailable");
      const rawResponse = await runtime.client.get<unknown>(
        "/v1/notifications?type=alert&limit=200"
      );
      return notificationsListResponseSchema.parse(rawResponse);
    },
    enabled:
      canViewNotificationData &&
      runtime.client !== null &&
      runtime.deploymentId === deploymentId &&
      runtime.operationGrants.some((grant) => grant.operation_id === "notifications_list"),
  });
  // TODO: Replace this deployment-wide status count with per-user notification read status.
  // Multiple delivery notifications can represent the same alert, so badges count
  // each unresolved alert source only once.
  const unresolvedAlertCount = useMemo(() => {
    const alertIds = new Set<string>();
    for (const notification of alertFeedQuery.data?.notifications ?? []) {
      if (notification.type === "alert" && notification.alert_status === "active") {
        alertIds.add(notification.source.id);
      }
    }
    return alertIds.size;
  }, [alertFeedQuery.data?.notifications]);
  const alertStatuses = useMemo<ReadonlyMap<string, AlertStatus | null>>(
    () =>
      new Map(
        (alertFeedQuery.data?.notifications ?? []).map((notification) => [
          notification.notification_id,
          notification.alert_status,
        ])
      ),
    [alertFeedQuery.data?.notifications]
  );

  const [selectedLiveNotificationId, setSelectedLiveNotificationId] = useState<string | null>(null);
  const [popupQueue, setPopupQueue] = useState<readonly NotificationPopupQueueItem[]>([]);
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;
  const markSeenRef = useRef(stream.markSeen);
  markSeenRef.current = stream.markSeen;

  // Reset all popup/selection state tied to this deployment's stream whenever
  // the deployment changes; the stream hook resets its own notifications/unseen state.
  useEffect(() => {
    setPopupQueue([]);
    setSelectedLiveNotificationId(null);
  }, [deploymentId]);

  // Every genuine addition enqueues exactly one popup. If the bell panel is
  // already open, treat it as viewed immediately so no stale unseen badge remains.
  useEffect(() => {
    if (stream.addedSeq === 0 || stream.lastAddedItem === null) {
      return;
    }
    const addedItem = stream.lastAddedItem;
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    void queryClient.invalidateQueries({ queryKey: ["notification-alert-count", deploymentId] });
    setPopupQueue((previous) => [
      ...previous,
      { popupId: `${addedItem.notification_id}:${stream.addedSeq}`, item: addedItem },
    ]);
    if (isPanelOpenRef.current) {
      markSeenRef.current([addedItem.notification_id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.addedSeq]);

  // A removal clears matching unseen (handled by the stream hook itself),
  // selected, and pending/visible popup state.
  useEffect(() => {
    if (stream.removedSeq === 0 || stream.lastRemovedId === null) {
      return;
    }
    const removedId = stream.lastRemovedId;
    setPopupQueue((previous) =>
      previous.filter((entry) => entry.item.notification_id !== removedId)
    );
    setSelectedLiveNotificationId((previous) => (previous === removedId ? null : previous));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.removedSeq]);

  const value = useMemo<NotificationCenterContextValue>(
    () => ({
      isPanelOpen,
      openPanel: () => {
        setIsPanelOpen(true);
        stream.markSeen(stream.notifications.map((item) => item.notification_id));
      },
      closePanel: () => {
        setIsPanelOpen(false);
        setSelectedLiveNotificationId(null);
      },
      togglePanel: () => {
        setIsPanelOpen((open) => {
          const nextOpen = !open;
          if (nextOpen) {
            stream.markSeen(stream.notifications.map((item) => item.notification_id));
          } else {
            setSelectedLiveNotificationId(null);
          }
          return nextOpen;
        });
      },

      liveNotifications: stream.notifications,
      unresolvedAlertCount,
      alertStatuses,
      selectedLiveNotificationId,
      selectLiveNotification: setSelectedLiveNotificationId,
      popupQueue,
      onExpireNotificationPopup: (popupId: string) => {
        setPopupQueue((previous) => previous.filter((entry) => entry.popupId !== popupId));
      },
      onSelectNotificationPopup: (popupId: string) => {
        const entry = popupQueue.find((candidate) => candidate.popupId === popupId);
        setPopupQueue((previous) => previous.filter((candidate) => candidate.popupId !== popupId));
        if (entry === undefined) {
          return;
        }
        setIsPanelOpen(false);
        setSelectedLiveNotificationId(null);
        stream.markSeen([entry.item.notification_id]);
        const alertQuery =
          entry.item.type === "alert" ? `?alertId=${encodeURIComponent(entry.item.source.id)}` : "";
        navigate(`/deployments/${encodeURIComponent(deploymentId)}/notifications${alertQuery}`);
      },
    }),
    [
      isPanelOpen,
      stream,
      selectedLiveNotificationId,
      popupQueue,
      unresolvedAlertCount,
      alertStatuses,
      deploymentId,
      navigate,
    ]
  );

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export function useNotificationCenter(): NotificationCenterContextValue {
  const value = useContext(NotificationCenterContext);

  if (!value) {
    throw new Error("useNotificationCenter must be used within a NotificationCenterProvider.");
  }

  return value;
}
